import {
  ArrowDownIcon,
  ArrowUpIcon,
  Wallet,
  ChartLine,
  HandCoins,
  TicketPercent
} from "../../icons";
import Badge from "../ui/badge/Badge";

// Interface para as props
interface EcommerceMetricsProps {
  summary?: {
    totalSales: number;
    netRevenue: number;
    grossRevenue: number;
    paidOrders: number;
    averageTicket: number;
    averageRevenuePerDay: number;
    comparison?: {
      totalSales: {
        previous: number;
        current: number;
        percentageChange: number;
      };
      paidOrders: {
        previous: number;
        current: number;
        percentageChange: number;
      };
      grossRevenue: {
        previous: number;
        current: number;
        percentageChange: number;
      };
      netRevenue: {
        previous: number;
        current: number;
        percentageChange: number;
      };
      averageTicket: {
        previous: number;
        current: number;
        percentageChange: number;
      };
      averageRevenuePerDay: {
        previous: number;
        current: number;
        percentageChange: number;
      };
    };
  };
  isLoading?: boolean;
}

// Função para formatar valores monetários em BRL
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(value);
};

export default function EcommerceMetrics({ summary, isLoading = false }: EcommerceMetricsProps) {
  // Valores padrão caso não haja dados ou esteja carregando
  const netRevenue = summary?.netRevenue || 0;
  const paidOrders = summary?.paidOrders || 0;
  const averageTicket = summary?.averageTicket || 0;
  const averageRevenuePerDay = summary?.averageRevenuePerDay || 0;
  const comparison = summary?.comparison;

  // Função para renderizar badge com seta e percentual
  const renderBadge = (percentageChange: number | undefined) => {
    if (percentageChange === undefined) return null;

    // Se for exatamente 0%, mostrar badge cinza sem ícone
    if (percentageChange === 0) {
      return (
        <Badge color="light">
          0.00%
        </Badge>
      );
    }

    const isPositive = percentageChange > 0;
    const color = isPositive ? 'success' : 'error';

    return (
      <Badge color={color}>
        {isPositive ? <ArrowUpIcon /> : <ArrowDownIcon />}
        {Math.abs(percentageChange).toFixed(2)}%
      </Badge>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 md:gap-6">
      {/* <!-- Metric Item Start --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-xl dark:bg-gray-800">
            <ChartLine className="text-gray-800 size-6 dark:text-white/90" />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Total em vendas
          </span>
        </div>
        <div className="flex items-end justify-between mt-4">
          <div>
            <h4 className="mt-2 me-1 font-bold text-gray-800 text-xl dark:text-white/90 whitespace-nowrap">
              {isLoading ? (
                <div className="h-6 bg-gray-200 animate-pulse rounded w-32 dark:bg-gray-700"></div>
              ) : (
                formatCurrency(netRevenue)
              )}
            </h4>
          </div>
          {renderBadge(comparison?.netRevenue.percentageChange)}
        </div>
        <p className="mt-1 text-gray-500 text-theme-xs dark:text-gray-400">
          {formatCurrency(comparison?.netRevenue.previous || 0)} período anterior
        </p>
      </div>
      {/* <!-- Metric Item End --> */}

      {/* <!-- Metric Item Start --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-xl dark:bg-gray-800">
            <HandCoins className="text-gray-800 size-6 dark:text-white/90" />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Número de vendas
          </span>
        </div>
        <div className="flex items-end justify-between mt-4">
          <div>
            <h4 className="mt-2 me-1 font-bold text-gray-800 text-xl dark:text-white/90 whitespace-nowrap">
              {isLoading ? (
                <div className="h-6 bg-gray-200 animate-pulse rounded w-32 dark:bg-gray-700"></div>
              ) : (
                paidOrders
              )}
            </h4>
          </div>
          {renderBadge(comparison?.paidOrders.percentageChange)}
        </div>
        <p className="mt-1 text-gray-500 text-theme-xs dark:text-gray-400">
          {comparison?.paidOrders.previous || 0} período anterior
        </p>
      </div>
      {/* <!-- Metric Item End --> */}

      {/* <!-- Metric Item Start --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-xl dark:bg-gray-800">
            <TicketPercent className="text-gray-800 size-6 dark:text-white/90" />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Ticket médio
          </span>
        </div>
        <div className="flex items-end justify-between mt-4">
          <div>
            <h4 className="mt-2 me-1 font-bold text-gray-800 text-xl dark:text-white/90 whitespace-nowrap">
              {isLoading ? (
                <div className="h-6 bg-gray-200 animate-pulse rounded w-24 dark:bg-gray-700"></div>
              ) : (
                formatCurrency(averageTicket)
              )}
            </h4>
          </div>
          {renderBadge(comparison?.averageTicket.percentageChange)}
        </div>
        <p className="mt-1 text-gray-500 text-theme-xs dark:text-gray-400">
          {formatCurrency(comparison?.averageTicket.previous || 0)} período anterior
        </p>
      </div>
      {/* <!-- Metric Item End --> */}

      {/* <!-- Metric Item Start --> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-xl dark:bg-gray-800">
            <Wallet className="text-gray-800 size-6 dark:text-white/90" />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Faturamento médio/dia
          </span>
        </div>
        <div className="flex items-end justify-between mt-4">
          <div>
            <h4 className="mt-2 me-1 font-bold text-gray-800 text-xl dark:text-white/90 whitespace-nowrap">
              {isLoading ? (
                <div className="h-6 bg-gray-200 animate-pulse rounded w-24 dark:bg-gray-700"></div>
              ) : (
                formatCurrency(averageRevenuePerDay)
              )}
            </h4>
          </div>
          {renderBadge(comparison?.averageRevenuePerDay.percentageChange)}
        </div>
        <p className="mt-1 text-gray-500 text-theme-xs dark:text-gray-400">
          {formatCurrency(comparison?.averageRevenuePerDay.previous || 0)} período anterior
        </p>
      </div>
      {/* <!-- Metric Item End --> */}
    </div>
  );
}
